/* eslint-disable react/prop-types */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  MapContainer,
  TileLayer,
  useMap,
  Marker,
  Polygon
} from 'react-leaflet';
import * as turf from '@turf/turf';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import SearchControl from './SearchControl';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import icon from 'leaflet/dist/images/marker-icon.png';
import CurrentLocationControl from './CurrentLocationControl';
import LocationInfoOverlay from './LocationInfoOverlay';
import Dexie from 'dexie';

// Initialize DexieDB for tile storage
const db = new Dexie('mapTilesDB');
db.version(1).stores({
  tiles: 'url, data, timestamp'
});

// Custom component to handle online/offline tile switching
// Updated MapTileLayer component for EnhancedMapPolygonEditor.jsx

const MapTileLayer = () => {
  const map = useMap();
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [hasCachedTiles, setHasCachedTiles] = useState(false);
  const mapboxLayerRef = useRef(null);
  const googleLayerRef = useRef(null);

  // Store mapbox token in localStorage
  useEffect(() => {
    const token = process.env.REACT_APP_MAPBOX_PUBLIC_KEY;
    if (token) {
      localStorage.setItem('mapbox_token', token);
    }
  }, []);

  // Check if we have cached tiles
  useEffect(() => {
    const checkCache = async () => {
      try {
        const count = await db.tiles.count();
        setHasCachedTiles(count > 0);
      } catch (err) {
        console.error('Error checking tile cache:', err);
      }
    };

    checkCache();
  }, []);

  // Set up listeners for online/offline events
  useEffect(() => {
    const handleConnectionChange = () => {
      setIsOnline(navigator.onLine);
    };

    window.addEventListener('online', handleConnectionChange);
    window.addEventListener('offline', handleConnectionChange);

    return () => {
      window.removeEventListener('online', handleConnectionChange);
      window.removeEventListener('offline', handleConnectionChange);
    };
  }, []);

  // Create Google Maps layer (for online use)
  useEffect(() => {
    if (!googleLayerRef.current) {
      googleLayerRef.current = L.tileLayer(
        `https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v12/tiles/{z}/{x}/{y}?access_token=${process.env.REACT_APP_MAPBOX_PUBLIC_KEY}`,
        {
          attribution:
            '&copy; <a href="https://www.mapbox.com/about/maps/">Mapbox</a>'
        }
      );
    }

    return () => {
      if (googleLayerRef.current && map.hasLayer(googleLayerRef.current)) {
        map.removeLayer(googleLayerRef.current);
      }
    };
  }, [map]);

  // Set up Mapbox offline layer with indexed DB
  useEffect(() => {
    if (!mapboxLayerRef.current) {
      // Get token from localStorage or environment
      const mapboxToken =
        localStorage.getItem('mapbox_token') ||
        process.env.REACT_APP_MAPBOX_PUBLIC_KEY;

      if (!mapboxToken) {
        console.error('Mapbox token not available for offline maps');
        return;
      }

      // Create custom tile layer for offline use
      const offlineTileLayer = L.TileLayer.extend({
        createTile: function (coords, done) {
          const tile = document.createElement('img');

          // Generate the URL in exactly the same format as when downloading
          const url = `https://api.mapbox.com/styles/v1/mapbox/satellite-streets-v12/tiles/${coords.z}/${coords.x}/${coords.y}?access_token=${mapboxToken}`;

          // Try to get from IndexedDB
          db.tiles
            .get(url)
            .then(cachedTile => {
              if (cachedTile && cachedTile.data) {
                // Convert array buffer to blob and create object URL
                const blob = new Blob([cachedTile.data]);
                const objectUrl = URL.createObjectURL(blob);

                tile.src = objectUrl;
                tile.onload = () => {
                  URL.revokeObjectURL(objectUrl); // Clean up
                  done(null, tile);
                };
                tile.onerror = e => {
                  console.error('Error loading tile from cache:', e);
                  createFallbackTile();
                };
              } else {
                createFallbackTile();
              }
            })
            .catch(err => {
              console.error('Error retrieving tile from IndexedDB:', err);
              createFallbackTile();
            });

          function createFallbackTile() {
            // Log the specific tile that's missing for debugging
            console.log(
              `Tile not found in cache: z=${coords.z}, x=${coords.x}, y=${coords.y}`
            );

            // Create a fallback tile with more useful information
            const canvas = document.createElement('canvas');
            canvas.width = 256;
            canvas.height = 256;
            const ctx = canvas.getContext('2d');

            // Fill background
            ctx.fillStyle = '#f2f2f2';
            ctx.fillRect(0, 0, 256, 256);

            // Add grid pattern to show tile boundaries
            ctx.strokeStyle = '#ddd';
            ctx.lineWidth = 1;
            ctx.beginPath();
            for (let i = 0; i < 256; i += 32) {
              ctx.moveTo(i, 0);
              ctx.lineTo(i, 256);
              ctx.moveTo(0, i);
              ctx.lineTo(256, i);
            }
            ctx.stroke();

            // Add text
            ctx.fillStyle = '#888';
            ctx.font = '12px Arial';
            ctx.textAlign = 'center';
            ctx.fillText(
              `Zoom: ${coords.z}, X: ${coords.x}, Y: ${coords.y}`,
              128,
              120
            );
            ctx.fillText('Tile not available offline', 128, 140);

            const dataUrl = canvas.toDataURL();
            tile.src = dataUrl;
            done(null, tile);
          }

          return tile;
        },

        // This limits the zoom level to prevent trying to load tiles that don't exist
        options: {
          minZoom: 8,
          maxZoom: 18, // Match the max zoom from download function
          attribution: '© Mapbox | Offline Mode'
        }
      });

      // Create the layer
      mapboxLayerRef.current = new offlineTileLayer('');
    }

    return () => {
      if (mapboxLayerRef.current && map.hasLayer(mapboxLayerRef.current)) {
        map.removeLayer(mapboxLayerRef.current);
      }
    };
  }, [map]);

  // Switch between online/offline layers
  useEffect(() => {
    // Clear any existing layers
    if (googleLayerRef.current && map.hasLayer(googleLayerRef.current)) {
      map.removeLayer(googleLayerRef.current);
    }

    if (mapboxLayerRef.current && map.hasLayer(mapboxLayerRef.current)) {
      map.removeLayer(mapboxLayerRef.current);
    }

    // Add the appropriate layer
    if (!isOnline && hasCachedTiles && mapboxLayerRef.current) {
      mapboxLayerRef.current.addTo(map);
      console.log('Added offline map layer');

      // Navigate to cached area
      navigateToCachedArea();
    } else if (googleLayerRef.current) {
      googleLayerRef.current.addTo(map);
      console.log('Added online Mapbox layer');
    }
  }, [isOnline, hasCachedTiles, map]);

  // Function to navigate to cached area - FIXED VERSION
  const navigateToCachedArea = async () => {
    try {
      // If polygon exists, center on its centroid
      if (
        window.__polygonVertices &&
        Array.isArray(window.__polygonVertices) &&
        window.__polygonVertices.length > 2
      ) {
        // Calculate centroid using turf
        const turfPolygon = turf.polygon([
          [...window.__polygonVertices, window.__polygonVertices[0]].map(
            ([lat, lng]) => [lng, lat]
          )
        ]);
        // Calculate polygon bounds for zoom
        const bounds = turf.bbox(turfPolygon); // [minLng, minLat, maxLng, maxLat]
        const southWest = [bounds[1], bounds[0]];
        const northEast = [bounds[3], bounds[2]];
        map.fitBounds([southWest, northEast], {
          maxZoom: 17,
          padding: [30, 30]
        });
        return;
      }

      // Find cached tiles
      const tiles = await db.tiles.toArray();
      if (tiles.length === 0) return;

      console.log(`Found ${tiles.length} cached tiles`);

      // Extract tile coordinates and zoom levels
      const tileData = [];
      tiles.forEach(tile => {
        const matches = tile.url.match(/\/(\d+)\/(\d+)\/(\d+)\?/);
        if (matches) {
          const zoom = parseInt(matches[1]);
          const x = parseInt(matches[2]);
          const y = parseInt(matches[3]);
          if (!isNaN(zoom) && !isNaN(x) && !isNaN(y)) {
            tileData.push({ zoom, x, y, url: tile.url });
          }
        }
      });

      if (tileData.length === 0) {
        console.warn('No valid tile coordinates found');
        return;
      }

      // Group by zoom level and find the one with most tiles (usually the target zoom level)
      const zoomGroups = {};
      tileData.forEach(tile => {
        if (!zoomGroups[tile.zoom]) {
          zoomGroups[tile.zoom] = [];
        }
        zoomGroups[tile.zoom].push(tile);
      });

      // Find zoom level with most tiles (this is likely the main download zoom level)
      const zoomLevels = Object.keys(zoomGroups)
        .map(Number)
        .sort((a, b) => a - b);
      const mainZoomLevel = zoomLevels.reduce((maxZoom, currentZoom) => {
        return zoomGroups[currentZoom].length > zoomGroups[maxZoom].length
          ? currentZoom
          : maxZoom;
      }, zoomLevels[0]);

      console.log(
        `Using zoom level ${mainZoomLevel} with ${zoomGroups[mainZoomLevel].length} tiles`
      );

      const tilesAtMainZoom = zoomGroups[mainZoomLevel];

      // Calculate bounds from tile coordinates
      let minX = Infinity,
        maxX = -Infinity;
      let minY = Infinity,
        maxY = -Infinity;

      tilesAtMainZoom.forEach(tile => {
        minX = Math.min(minX, tile.x);
        maxX = Math.max(maxX, tile.x);
        minY = Math.min(minY, tile.y);
        maxY = Math.max(maxY, tile.y);
      });

      console.log(
        `Tile bounds: X(${minX}-${maxX}), Y(${minY}-${maxY}), Zoom: ${mainZoomLevel}`
      );

      // Convert tile coordinates to lat/lng bounds
      // Using correct Web Mercator projection formulas
      const tileToLatLng = (x, y, z) => {
        const n = Math.pow(2, z);
        const lng = (x / n) * 360 - 180;
        const lat_rad = Math.atan(Math.sinh(Math.PI * (1 - (2 * y) / n)));
        const lat = (lat_rad * 180) / Math.PI;
        return [lat, lng];
      };

      // Get corners of the bounding box
      const northWest = tileToLatLng(minX, minY, mainZoomLevel);
      const southEast = tileToLatLng(maxX + 1, minY + 1, mainZoomLevel);

      // Calculate center point
      const centerLat = (northWest[0] + southEast[0]) / 2;
      const centerLng = (northWest[1] + southEast[1]) / 2;

      console.log(`Calculated center: ${centerLat}, ${centerLng}`);

      // Create bounds and fit map to show all cached tiles
      const bounds = L.latLngBounds([
        [southEast[0], northWest[1]], // Southwest corner
        [northWest[0], southEast[1]] // Northeast corner
      ]);

      // Fit the map to bounds with some padding
      map.fitBounds(bounds, {
        padding: [20, 20], // Add some padding around the edges
        maxZoom: Math.min(mainZoomLevel + 1, 18) // Limit max zoom
      });

      console.log('Map centered on cached area');
    } catch (err) {
      console.error('Error navigating to cached area:', err);
    }
  };

  return null; // No need to render anything, layers added directly to map
};

// Offline mode indicator
const OfflineIndicator = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  useEffect(() => {
    const handleOnlineStatus = () => {
      setIsOnline(navigator.onLine);
    };

    window.addEventListener('online', handleOnlineStatus);
    window.addEventListener('offline', handleOnlineStatus);

    return () => {
      window.removeEventListener('online', handleOnlineStatus);
      window.removeEventListener('offline', handleOnlineStatus);
    };
  }, []);

  if (isOnline) return null;

  return (
    <div
      className="leaflet-control leaflet-bar offline-indicator"
      style={{
        position: 'absolute',
        top: '10px',
        right: '10px',
        backgroundColor: 'rgba(255, 0, 0, 0.7)',
        color: 'white',
        padding: '5px 10px',
        borderRadius: '4px',
        zIndex: 1000,
        fontSize: '12px',
        fontWeight: 'bold'
      }}
    >
      Offline Mode - Using Downloaded Maps
    </div>
  );
};

// Default marker icon
const defaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41]
});

// Create vertex marker icon
const createVertexIcon = (color = 'red') =>
  L.divIcon({
    className: 'custom-vertex-marker',
    html: `
      <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" fill="${color}" viewBox="0 0 16 16">
        <path d="M8 16s6-5.686 6-10A6 6 0 0 0 2 6c0 4.314 6 10 6 10m0-7a3 3 0 1 1 0-6 3 3 0 0 1 0 6"/>
      </svg>
    `,
    iconSize: [24, 24],
    iconAnchor: [18, 36]
  });

// Create center marker icon
const createCenterIcon = () =>
  L.divIcon({
    className: 'center-marker',
    html: `
    <div style="
      width: 30px;
      height: 30px;
      border: 3px solid blue;
      border-radius: 50%;
      background: rgba(0,0,255,0.2);
      display: flex;
      align-items: center;
      justify-content: center;
    ">
      <div style="
        width: 5px;
        height: 5px;
        background-color: blue;
        border-radius: 50%;
      "></div>
    </div>
  `,
    iconSize: [30, 30],
    iconAnchor: [15, 15]
  });

// Center marker component
const CenterMarker = () => {
  const map = useMap();
  const [centerCoords, setCenterCoords] = useState(map.getCenter());

  useEffect(() => {
    const handleMove = () => {
      setCenterCoords(map.getCenter());
    };

    map.on('move', handleMove);
    return () => {
      map.off('move', handleMove);
    };
  }, [map]);

  return (
    <Marker
      position={centerCoords}
      icon={createCenterIcon()}
      interactive={false}
    />
  );
};

// EnhancedMapPolygonEditor main component
const EnhancedMapPolygonEditor = ({
  userLocation,
  uploadedGeoJSONLayer,
  onPolygonChange,
  initialCenter = [9.5937, 78.9629],
  setSelectedLocation,
  isMapEditable,
  handleSearchSelect,
  selectedLocation,
  mainMode,
  addressDetails
}) => {
  const mapRef = useRef(null);
  const [vertices, setVertices] = useState([]);
  const [mode, setMode] = useState('view');
  const [areaHectares, setAreaHectares] = useState(0);
  const [areaAcres, setAreaAcres] = useState(0);
  const [perimeter, setPerimeter] = useState(0);
  const [isOnline, setIsOnline] = useState(navigator.onLine);

  // Monitor online/offline status
  useEffect(() => {
    const handleOnlineStatus = () => {
      setIsOnline(navigator.onLine);
    };

    window.addEventListener('online', handleOnlineStatus);
    window.addEventListener('offline', handleOnlineStatus);

    return () => {
      window.removeEventListener('online', handleOnlineStatus);
      window.removeEventListener('offline', handleOnlineStatus);
    };
  }, []);

  // Coordinate conversion utility: GeoJSON to vertices
  const convertGeoJSONToVertices = useCallback(geoJSON => {
    if (!geoJSON?.geometry?.coordinates?.[0]) return [];

    // Remove closing point if it's a duplicate
    const coords = geoJSON.geometry.coordinates[0].map(coord => [
      coord[1],
      coord[0]
    ]);
    return coords[0][0] === coords[coords.length - 1][0] &&
      coords[0][1] === coords[coords.length - 1][1]
      ? coords.slice(0, -1)
      : coords;
  }, []);

  // Coordinate conversion utility: vertices to GeoJSON
  const convertVerticesToGeoJSON = useCallback(coords => {
    if (coords.length < 3) return null;

    const closedCoords = [...coords, coords[0]];
    return {
      type: 'Feature',
      geometry: {
        type: 'Polygon',
        coordinates: [closedCoords.map(coord => [coord[1], coord[0]])]
      },
      properties: {}
    };
  }, []);

  // Calculate area of polygon
  const calculateArea = useCallback(
    coords => {
      if (coords.length < 3) {
        setAreaHectares(0);
        setAreaAcres(0);
        onPolygonChange?.(null);
        return;
      }

      const geoJSON = convertVerticesToGeoJSON(coords);

      try {
        const areaSquareMeters = turf.area(geoJSON);
        const hectares = areaSquareMeters / 10000;
        const acres = areaSquareMeters / 4046.8564224;
        const line = turf.lineString(geoJSON.geometry.coordinates[0]);
        const perimeter = turf.length(line, { units: 'meters' });

        setPerimeter(perimeter);
        setAreaHectares(hectares);
        setAreaAcres(acres);
        onPolygonChange?.(geoJSON);
      } catch (error) {
        console.error('Area calculation error:', error);
        setAreaHectares(0);
        setAreaAcres(0);
      }
    },
    [convertVerticesToGeoJSON, onPolygonChange]
  );

  // Initialize vertices from uploaded GeoJSON
  useEffect(() => {
    if (uploadedGeoJSONLayer) {
      const initialVertices = convertGeoJSONToVertices(uploadedGeoJSONLayer);
      setVertices(initialVertices);
      calculateArea(initialVertices);
      // Expose vertices globally for offline map centering
      window.__polygonVertices = initialVertices;

      // Fit bounds for the uploaded layer
      if (mapRef.current) {
        try {
          const leafletLayer = L.geoJSON(uploadedGeoJSONLayer);
          mapRef.current.fitBounds(leafletLayer.getBounds(), {
            padding: [20, 20],
            maxZoom: 18
          });
        } catch (error) {
          console.error('Error fitting bounds:', error);
        }
      }
    } else {
      window.__polygonVertices = undefined;
    }
  }, [uploadedGeoJSONLayer, convertGeoJSONToVertices, calculateArea]);

  // Handler to add a vertex to the polygon
  const handleAddVertex = useCallback(
    coords => {
      if (mode !== 'draw') return;
      const normalizedCoords = Array.isArray(coords)
        ? coords
        : [coords.lat, coords.lng];

      const newVertices = [...vertices, normalizedCoords];
      setVertices(newVertices);
      calculateArea(newVertices);
    },
    [vertices, mode, calculateArea]
  );

  // Handler to delete the last vertex from the polygon
  const handleDeleteLastVertex = useCallback(() => {
    const newVertices = vertices.slice(0, -1);
    setVertices(newVertices);
    calculateArea(newVertices);
  }, [vertices, calculateArea]);

  // Handler to clear the polygon
  const handleClearPolygon = useCallback(() => {
    setVertices([]);
    setPerimeter(0);
    setAreaHectares(0);
    setAreaAcres(0);
    onPolygonChange?.(null);
  }, [onPolygonChange]);

  // Center the map on the user's location when it's available (only if online)
  useEffect(() => {
    if (
      isOnline &&
      mapRef.current &&
      userLocation &&
      userLocation.lat &&
      userLocation.lng
    ) {
      mapRef.current.setView([userLocation.lat, userLocation.lng], 20, {
        animate: false
      });
    }
  }, [userLocation, isOnline]);

  // Stop any Leaflet animation on unmount to prevent _leaflet_pos errors
  useEffect(() => {
    return () => {
      if (mapRef.current) {
        try {
          mapRef.current.stop();
        } catch (_) {
          // ignore leaflet stop errors on unmount
        }
      }
    };
  }, []);

  return (
    <div className="relative w-full h-[500px]">
      <MapContainer
        ref={mapRef}
        center={
          isOnline && userLocation && userLocation.lat
            ? [userLocation.lat, userLocation.lng]
            : initialCenter
        }
        zoom={16}
        style={{ height: '72vh', width: '100%' }}
        whenReady={mapInstance => (mapRef.current = mapInstance.target)}
        zoomControl={false}
      >
        {/* Custom map tile handler for online/offline switching */}
        <MapTileLayer />

        {/* Offline indicator */}
        <OfflineIndicator />

        {/* Only show location controls when online and editable */}
        {isMapEditable && isOnline && (
          <CurrentLocationControl onLocationFound={handleSearchSelect} />
        )}

        {isMapEditable && isOnline && (
          <SearchControl
            onLocationSelect={({ lat, lng, address, accuracy }) =>
              setSelectedLocation({
                lat,
                lng,
                address,
                accuracy
              })
            }
          />
        )}

        {/* Selected location marker */}
        {selectedLocation && isMapEditable && isOnline && (
          <Marker
            position={[selectedLocation.lat, selectedLocation.lng]}
            icon={defaultIcon}
          />
        )}

        {/* Center marker for polygon mode */}
        {mainMode === 'polygon' && isMapEditable && <CenterMarker />}

        {/* Display polygon if we have enough vertices */}
        {vertices.length > 2 && (
          <Polygon
            positions={vertices}
            pathOptions={{
              color: 'red',
              fillColor: 'red',
              fillOpacity: 0.2,
              weight: 2
            }}
          />
        )}

        {/* Display markers for each vertex */}
        {vertices.map((vertex, index) => (
          <Marker
            key={`vertex-${index}`}
            position={vertex}
            icon={createVertexIcon()}
          />
        ))}

        {/* Display info overlay based on mode */}
        {mainMode === 'polygon' && (
          <LocationInfoOverlay
            mode="polygon"
            location={selectedLocation}
            areaHectares={areaHectares}
            areaAcres={areaAcres}
            perimeter={perimeter}
          />
        )}

        {mainMode === 'location' && (
          <LocationInfoOverlay
            mode="location"
            location={selectedLocation}
            address={selectedLocation?.address}
            style={{ padding: '40px' }}
          />
        )}
      </MapContainer>

      {/* Polygon editing controls */}
      {mainMode === 'polygon' && isMapEditable && (
        <div className="d-flex gap-2 z-index-1000 mt-1">
          <button
            onClick={() => setMode(mode === 'draw' ? 'view' : 'draw')}
            className={`btn ${mode === 'draw' ? 'btn-danger' : 'btn-primary'}`}
          >
            {mode === 'draw' ? 'Stop Drawing' : 'Start Drawing'}
          </button>

          {mode === 'draw' && (
            <button
              onClick={() => {
                if (mapRef.current) {
                  handleAddVertex(mapRef.current.getCenter());
                }
              }}
              disabled={mode !== 'draw' || !mapRef.current}
              className="btn btn-success"
            >
              Add Vertex
            </button>
          )}

          {mode === 'draw' && (
            <button
              onClick={handleDeleteLastVertex}
              disabled={vertices.length === 0}
              className="btn btn-warning"
            >
              Delete Last
            </button>
          )}

          <button
            onClick={handleClearPolygon}
            disabled={vertices.length === 0}
            className="btn btn-danger"
          >
            Clear All
          </button>
        </div>
      )}
    </div>
  );
};

export default EnhancedMapPolygonEditor;
